import CONSTANT from "./constant";

/**
 * Helper class for resolving the Kyriba transaction code written into the wire export file.
 *
 * The rules are evaluated in order and the first match wins:
 *   1. Interbank domestic settlement            -> ICBK
 *   2. Low-value domestic INR settlement        -> ICLW
 *   3. Same currency, same country              -> ICDT
 *      Same currency, different country         -> ICCB
 *   4. Cross-currency                           -> ICFX
 *
 * Extracted from queryApprovedPaymentData so the rules can be unit tested; the component
 * still owns resolving the country codes from companyCodeToKyribaBankAccountsMap.
 */
class kyribaTransactionCodeUtil {

    /**
     * A TRS bank account identifier carries a two-character bank code at a fixed offset,
     * e.g. J3USD_HS0750 and B07USDWF2487 -> HS and WF. Both the two-character company code
     * plus separator form (DWCOP_CB0029) and the three-character company code form
     * (106COPCB5011) place the bank code at the same offset. The Colombian COP examples were
     * confirmed against production accounts on P510870013.
     */
    static BANK_CODE_START_INDEX = 6;
    static BANK_CODE_LENGTH = 2;

    static BANK_CODE_HSBC = "HS";
    // CB = Citi Bank, used by Colombian COP accounts (P510870013).
    static BANK_CODE_CB = "CB";

    static COUNTRY_CODE_JAPAN = "JP";
    static COUNTRY_CODE_COLOMBIA = "CO";
    static COUNTRY_CODE_INDIA = "IN";

    static CURRENCY_JPY = "JPY";
    static CURRENCY_COP = "COP";
    static CURRENCY_INR = "INR";

    /** INR settlements below this amount are treated as low value and coded ICLW. */
    static INR_LOW_VALUE_CEILING = 200000;

    /**
     * Whether the account is held at the bank identified by the given two-character bank code.
     * @param account the TRS bank account identifier
     * @param bankCode the two-character bank code to match
     */
    static isBank = (account, bankCode) => {
        if (account === null || account === undefined || account === '') {
            return false;
        }

        return account.substr(this.BANK_CODE_START_INDEX, this.BANK_CODE_LENGTH) === bankCode;
    }

    static isBankHSBC = (account) => this.isBank(account, this.BANK_CODE_HSBC);

    static isBankCB = (account) => this.isBank(account, this.BANK_CODE_CB);

    /**
     * Interbank (ICBK) applies where a domestic settlement is booked between two accounts at
     * the same bank, in a market whose bank requires the interbank code:
     *   - Japan:    JPY between two HSBC accounts, both in JP
     *   - Colombia: COP between two CB accounts, both in CO (P510870013)
     * Both markets must keep returning ICBK; neither is a replacement for the other.
     */
    static isInterbankSettlement = (targetCCY, sendingAccount, receivingAccount,
                                    sendingCountryCode, receivingCountryCode) => {

        const isJapanHsbcSettlement = targetCCY === this.CURRENCY_JPY
            && this.isBankHSBC(sendingAccount)
            && this.isBankHSBC(receivingAccount)
            && sendingCountryCode === this.COUNTRY_CODE_JAPAN
            && receivingCountryCode === this.COUNTRY_CODE_JAPAN;

        const isColombiaCbSettlement = targetCCY === this.CURRENCY_COP
            && this.isBankCB(sendingAccount)
            && this.isBankCB(receivingAccount)
            && sendingCountryCode === this.COUNTRY_CODE_COLOMBIA
            && receivingCountryCode === this.COUNTRY_CODE_COLOMBIA;

        return isJapanHsbcSettlement || isColombiaCbSettlement;
    }

    /**
     * Whether the settlement is a low-value domestic INR settlement, coded ICLW.
     */
    static isLowValueDomesticInrSettlement = (targetCCY, targetAmount,
                                              sendingCountryCode, receivingCountryCode) => {
        return targetCCY === this.CURRENCY_INR
            && sendingCountryCode === this.COUNTRY_CODE_INDIA
            && receivingCountryCode === this.COUNTRY_CODE_INDIA
            && targetAmount < this.INR_LOW_VALUE_CEILING;
    }

    /**
     * Resolve the Kyriba transaction code for a settlement.
     * @param sendingAccount sending bank account identifier
     * @param receivingAccount receiving bank account identifier
     * @param sendingCCY currency of the sending account
     * @param targetCCY currency the settlement is paid in
     * @param targetAmount amount the settlement is paid in
     * @param sendingCountryCode country of the sending account
     * @param receivingCountryCode country of the receiving account
     */
    static resolve = ({ sendingAccount, receivingAccount, sendingCCY, targetCCY, targetAmount,
                        sendingCountryCode, receivingCountryCode }) => {

        if (this.isInterbankSettlement(targetCCY, sendingAccount, receivingAccount,
                                       sendingCountryCode, receivingCountryCode)) {
            return CONSTANT.KT_CODE_ICBK;
        }

        if (this.isLowValueDomesticInrSettlement(targetCCY, targetAmount,
                                                 sendingCountryCode, receivingCountryCode)) {
            return CONSTANT.KT_CODE_ICLW;
        }

        if (targetCCY === sendingCCY) {
            if (sendingCountryCode === receivingCountryCode) {
                return CONSTANT.KT_CODE_ICDT;
            } else {
                return CONSTANT.KT_CODE_ICCB;
            }
        } else {
            return CONSTANT.KT_CODE_ICFX;
        }
    }
}

export default kyribaTransactionCodeUtil;
